import { useState } from 'react';
import CustomButton from '../../reusable/CustomButton';
import PrintIcon from '../../assets/print.svg';
import { Transcript, TranscriptPrintData, TranscriptPrintError } from '../../../interfaces/TranscriptsInterface';
import useFetchData from '../../hooks/useFetchData';
import { getTranscriptsDetails } from '../../../services/transcriptsService';
import useSchools from '../../hooks/useSchools';
import TranscriptPrintView from './TranscriptPrintView';
import TranscriptsCoverPage from './TranscriptsCoverPage';
import TranscriptsByStudentInstructionsPage from './TranscriptsByStudentInstructionsPage';
import TranscriptsCollegeEligibilityPage from './TranscriptsCollegeEligibilityPage';
import { getSubjectAreas, getCollegeEligibilityFrameworks } from '../../../services/eoaConfigServices';
import PrintContainer from '../../reusable/PrintContainer';

interface TranscriptsPrintByStudentButtonProps {
  transcripts: Transcript[];
}

const TranscriptsPrintByStudentButton = ({ transcripts }: TranscriptsPrintByStudentButtonProps) => {
  const { transcriptSelectionSchool, schools } = useSchools();
  const schoolName = schools.data?.find((s) => s.id === transcriptSelectionSchool)?.name ?? '';

  const firstTranscriptId = transcripts.length > 0 ? transcripts[0].id : false;

  const [shouldFetch, setShouldFetch] = useState(false);

  const { isPending, error, data } = useFetchData({
    fetchData: async (eoaId: string, access_token: string) => {
      const result = await getTranscriptsDetails(
        eoaId,
        access_token,
        transcriptSelectionSchool,
        transcripts.map((t) => t.id)
      );

      const printData = [] as Array<TranscriptPrintData | TranscriptPrintError>;
      result.forEach((request, index) => {
        if (request.status === 'fulfilled') {
          printData.push({ type: 'data', data: request.value.data });
        } else {
          printData.push({ studentNumber: transcripts[index].school_student_number, type: 'error' });
        }
      });

      return { data: printData };
    },
    options: {
      dependencies: [firstTranscriptId, shouldFetch],
      areDependenciesRequired: true,
    },
    errorMessage: 'Error occurred while fetching transcripts details!',
    initialState: [],
  });

  const {
    isPending: isSubjectAreasDataPending,
    error: subjectAreasDataError,
    data: subjectAreasData,
  } = useFetchData({
    fetchData: (_eoaId, access_token) => getSubjectAreas(access_token),
    errorMessage: 'Error occurred while fetching subject areas!',
    options: { dependencies: [shouldFetch] },
    initialState: [],
  });

  const {
    isPending: isFrameworksPending,
    error: frameworksError,
    data: frameworksData,
  } = useFetchData({
    fetchData: (eoaId, access_token) => getCollegeEligibilityFrameworks(eoaId, access_token),
    errorMessage: 'Error occurred while fetching college eligibility frameworks!',
    options: { dependencies: [shouldFetch] },
    initialState: { frameworks: [] },
  });

  if (error || subjectAreasDataError || frameworksError) return <p>Print unavailable!</p>;

  const isFetching = shouldFetch && (isPending || isSubjectAreasDataPending || isFrameworksPending);
  const isContentReady = shouldFetch && !isPending && !isSubjectAreasDataPending && !isFrameworksPending;

  return (
    <PrintContainer
      isContentReady={isContentReady}
      onPrintRequested={() => setShouldFetch(true)}
      documentTitle="XQ EOAA transcripts (student view)"
      printButton={(handlePrint) => (
        <CustomButton
          variant="text"
          buttonText="Print by student"
          icon={<img src={PrintIcon} alt="print by student" />}
          iconPosition="start"
          buttonAction={handlePrint}
          loading={isFetching}
          shouldShowUpdates={true}
        />
      )}
    >
      <>
        <TranscriptsCoverPage variant="by-student" schoolName={schoolName} />
        <TranscriptsByStudentInstructionsPage />
        <TranscriptsCollegeEligibilityPage variant="by-student" frameworks={frameworksData.frameworks} />
        {data.map((transcriptDetails, index) => (
          <TranscriptPrintView
            key={index}
            transcript={transcriptDetails}
            subjectAreas={subjectAreasData.map(({ name }) => name)}
          />
        ))}
      </>
    </PrintContainer>
  );
};

export default TranscriptsPrintByStudentButton;
